<template>
	<div>
		<b-row class="mb-2">
			<!-- Date picker -->
			<b-col cols="12" md="6" class="pr-2">
				<b-row>
					<b-col md="5">
						<label for="from-date">From Date</label>
						<b-form-datepicker
							id="from-date"
							:date-format-options="{
								year: 'numeric',
								month: 'short',
								day: '2-digit',
								weekday: 'short',
							}"
							placeholder="Choose a date"
							locale="en"
							v-model="search_filter.from_date"
						/>
					</b-col>

					<b-col md="5 p-0">
						<label for="to-date">To Date</label>
						<b-form-datepicker
							id="to-date"
							:date-format-options="{
								year: 'numeric',
								month: 'short',
								day: '2-digit',
								weekday: 'short',
							}"
							placeholder="Choose a date"
							locale="en"
							v-model="search_filter.to_date"
						/>
					</b-col>

					<b-col md="1 d-flex align-items-end">
						<b-button @click="getRawMaterialPurchaseList()" variant="primary">Filter</b-button>
					</b-col>
				</b-row>
			</b-col>

			<!-- Search -->
			<b-col cols="12" md="6" class="pl-5 mt-2 d-flex justify-content-end align-items-center">
				<div>
					<b-form-input
						type="text"
						class="form-control"
						placeholder="Search"
						@input="getRawMaterialPurchaseList()"
						v-model.trim="search_filter.input_field"
						style="border-radius: 4px"
					/>
				</div>

				<b-button @click="clearFilter" variant="secondary ml-1">Clear</b-button>

				<b-button v-if="hasPermission('add_company_purchase')" :to="{ name: 'apps-company-purchase-raw-material-add' }" variant="primary mr-1 ml-2"> Add </b-button>

				<b-button @click="excelDownload()" variant="warning">
					<feather-icon icon="DownloadIcon" size="16" />
				</b-button>
			</b-col>
		</b-row>

		<div v-if="purchaseList && purchaseList.total > 0">
			<b-table
				id="rawMaterialPurchaseList"
				:items="purchaseList.data"
				:fields="fields"
				responsive
				show-empty
				empty-text="No matching records found"
				class="position-relative"
			>
				<!-- S.no -->
				<template #cell(index)="data">
					<div class="ml-1">
						{{ purchaseList.from + data.index }}
					</div>
				</template>

				<!-- Purchase date -->
				<template #cell(created_at)="data">
					{{ formatDate(data.item.created_at) }}
				</template>

				<!-- Raw Material name (items[0].raw_material.name) -->
				<template #cell(raw_material)="data">
					<span v-if="hasItem(data.item) && getItem(data.item).raw_material && getItem(data.item).raw_material.name">
						{{ getItem(data.item).raw_material.name }}
					</span>
					<span v-else>NA</span>
				</template>

				<!-- Unit (items[0].raw_material.unit.name) -->
				<template #cell(unit)="data">
					<span
						v-if="
							hasItem(data.item) &&
							getItem(data.item).raw_material &&
							getItem(data.item).raw_material.unit &&
							getItem(data.item).raw_material.unit.name
						"
					>
						{{ getItem(data.item).raw_material.unit.name }}
					</span>
					<span v-else>NA</span>
				</template>

				<!-- Quantity (items[0].quantity) -->
				<template #cell(quantity)="data">
					<span
						v-if="
							hasItem(data.item) && getItem(data.item).quantity !== undefined && getItem(data.item).quantity !== null
						"
					>
						{{ getItem(data.item).quantity }}
					</span>
					<span v-else>0</span>
				</template>

				<!-- Unit Cost (items[0].unit_cost) -->
				<template #cell(unit_cost)="data">
					<span
						v-if="
							hasItem(data.item) && getItem(data.item).unit_cost !== undefined && getItem(data.item).unit_cost !== null
						"
					>
						{{ getItem(data.item).unit_cost }}
					</span>
					<span v-else>0</span>
				</template>

				<!-- Total Cost (quantity * unit_cost) -->
				<template #cell(total_cost)="data">
					<span v-if="hasItem(data.item)">
						{{ calcTotal(getItem(data.item).quantity, getItem(data.item).unit_cost) }}
					</span>
					<span v-else>0.00</span>
				</template>
			</b-table>

			<div class="mt-2 mb-5">
				<b-pagination
					:value="purchaseList.current_page"
					:total-rows="purchaseList.total"
					:per-page="purchaseList.per_page"
					@change="getRawMaterialPurchaseList"
					align="end"
				/>
			</div>
		</div>

		<div v-else class="d-flex flex-column align-items-center py-5">
			<b-img :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')" class="mr-2" />
			<h4 class="mt-2 Secondary">Raw Material Purchase List Empty</h4>
		</div>
	</div>
</template>

<script>
import vSelect from "vue-select";
import axiosIns from "@/libs/axios";
import moment from "moment";

export default {
	components: { vSelect },
	data() {
		return {
			fields: [
				{ key: "index", label: "S.no" },
				{ key: "created_at", label: "Purchase Date" },
				{ key: "raw_material", label: "Raw Material" },
				{ key: "unit", label: "Unit" },
				{ key: "quantity", label: "Quantity" },
				{ key: "unit_cost", label: "Unit Cost" },
				{ key: "total_cost", label: "Total Cost" },
			],
			search_filter: {
				input_field: "",
				to_date: "",
				from_date: "",
			},
			purchaseList: {},
			userData: null,
		};
	},
	created() {
		this.userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
		this.getRawMaterialPurchaseList();
	},
	methods: {
		// helpers to avoid optional chaining in templates
		hasItem(row) {
			return row && row.items && row.items.length > 0 && row.items[0];
		},
		getItem(row) {
			// return first item safely
			return row && row.items && row.items.length ? row.items[0] : {};
		},
		calcTotal(qty, rate) {
			const q = Number(qty || 0);
			const r = Number(rate || 0);
			return (q * r).toFixed(2);
		},
		formatDate(value) {
			return value ? moment(value).format("MMMM Do YYYY") : "NA";
		},

		getRawMaterialPurchaseList(pageNo = 1) {
			const companyId = this.userData && this.userData.company ? this.userData.company.id : null;
			const url = `web/company/${companyId}/rm-purchases`;

			const queryParams = { page: pageNo };
			if (this.search_filter.input_field) queryParams.input_field = this.search_filter.input_field;
			if (this.search_filter.to_date) queryParams.to_date = this.search_filter.to_date;
			if (this.search_filter.from_date) queryParams.from_date = this.search_filter.from_date;

			axiosIns
				.get(this.addQueryString(url, queryParams))
				.then((response) => {
					// API shape from your screenshot
					this.purchaseList = response.data.purchases;
				})
				.catch((error) => {
					// avoid $refs.setErrors if not defined
					// eslint-disable-next-line no-console
					console.error(error && error.response ? error.response?.data : error);
				});
		},

		excelDownload() {
			// Build payload without mutating visible filters
			const payload = { ...this.search_filter };
			if (payload.from_date && payload.to_date) {
				payload.from_date = moment(payload.from_date).format("YYYY-MM-DD");
				payload.to_date = moment(payload.to_date).format("YYYY-MM-DD");
			}
			const order_filter_data = this.prepareForm(payload);

			axiosIns
				.post(`web/export-company-rm-purchases`, order_filter_data)
				.then((response) => {
					const tempLink = document.createElement("a");
					tempLink.style.display = "none";
					tempLink.href = response.data.file;
					tempLink.setAttribute("download", response.data.name);
					if (typeof tempLink.download === "undefined") {
						tempLink.setAttribute("target", "_blank");
					}
					document.body.appendChild(tempLink);
					tempLink.click();
					document.body.removeChild(tempLink);

					this.getRawMaterialPurchaseList(
						this.purchaseList && this.purchaseList.current_page ? this.purchaseList.current_page : 1
					);
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error(error && error.response ? error.response?.data : error);
				});
		},

		clearFilter() {
			this.search_filter.input_field = "";
			this.search_filter.to_date = "";
			this.search_filter.from_date = "";
			this.getRawMaterialPurchaseList(1);
		},
	},
};
</script>

<style lang="scss" scoped>
.activeClass {
	background-color: #296db4;
	color: #fff;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>